import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import useSwipeGesture from '../../../hooks/useSwipeGesture';

const WeeklyChart = ({ workoutLogs = [], loading = false }) => {
  const [activeChart, setActiveChart] = useState('steps');

  const chartOptions = [
    { key: 'steps', label: 'Steps', color: '#5DB075', icon: 'Footprints' },
    { key: 'calories', label: 'Calories', color: '#F59E0B', icon: 'Flame' },
    { key: 'water', label: 'Water', color: '#3B82F6', icon: 'Droplets' },
    { key: 'sleep', label: 'Sleep', color: '#8B5CF6', icon: 'Moon' }
  ];

  const currentIndex = chartOptions?.findIndex(o => o?.key === activeChart);

  const swipe = useSwipeGesture({
    onSwipeLeft: () => {
      const next = (currentIndex + 1) % chartOptions?.length;
      setActiveChart(chartOptions?.[next]?.key);
    },
    onSwipeRight: () => {
      const prev = (currentIndex - 1 + chartOptions?.length) % chartOptions?.length;
      setActiveChart(chartOptions?.[prev]?.key);
    },
  });

  // Build last 7 days chart data from real workout logs
  const weeklyData = useMemo(() => {
    const days = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date?.setDate(today?.getDate() - i);
      const dateStr = date?.toISOString()?.split('T')?.[0];
      const dayLabel = date?.toLocaleDateString('en-US', { weekday: 'short' });

      const dayLogs = workoutLogs?.filter(log => log?.date === dateStr) || [];
      const steps = dayLogs?.reduce((sum, log) => sum + (log?.steps || 0), 0);
      const calories = dayLogs?.reduce((sum, log) => sum + (log?.calories || 0), 0);

      days?.push({
        day: dayLabel,
        date: dateStr,
        steps,
        calories,
        water: 0,
        sleep: 0
      });
    }

    return days;
  }, [workoutLogs]);

  const activeOption = chartOptions?.find(option => option?.key === activeChart);
  const hasData = weeklyData?.some(d => d?.[activeChart] > 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border/40 rounded-xl p-3 wellness-elevation-2">
          <p className="wellness-text-accent mb-1">{label}</p>
          <p className="wellness-text-primary" style={{ color: activeOption?.color }}>
            {`${activeOption?.label}: ${payload?.[0]?.value?.toLocaleString()}${
              activeChart === 'steps' ? ' steps' : 
              activeChart === 'calories' ? ' kcal' :
              activeChart === 'water' ? ' glasses' : ' hours'
            }`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header Section */}
      <div className="wellness-header flex-shrink-0 min-h-[60px]">
        <h3 className="wellness-title-tertiary text-constrained">Weekly Progress</h3>
        
        {/* Chart Type Selector */}
        <div className="flex items-center flex-wrap wellness-gap-xs">
          {chartOptions?.map((option) => (
            <motion.button
              key={option?.key}
              onClick={() => setActiveChart(option?.key)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 touch-target-sm ${
                activeChart === option?.key
                  ? 'bg-primary text-primary-foreground wellness-elevation-1'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-wellness-light'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon name={option?.icon} size={16} className="flex-shrink-0" />
              <span className="hidden sm:inline text-constrained">{option?.label}</span>
            </motion.button>
          ))}
        </div>
      </div>
      {/* Swipe indicator dots — mobile only */}
      <div className="flex md:hidden justify-center gap-1.5 mb-2">
        {chartOptions?.map((option, idx) => (
          <button
            key={option?.key}
            onClick={() => setActiveChart(option?.key)}
            className="touch-target-dot"
            aria-label={`Switch to ${option?.label} chart`}
          >
            <span
              className={`block w-2 h-2 rounded-full transition-all duration-200 ${
                activeChart === option?.key
                  ? 'bg-primary scale-125' :'bg-muted-foreground/30'
              }`}
            />
          </button>
        ))}
      </div>
      {/* Chart Container — swipeable on mobile */}
      <div
        className="flex-1 min-h-0 mb-4 select-none"
        {...swipe}
      >
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex flex-col items-center space-y-3">
              <motion.div
                className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <p className="wellness-text-secondary text-sm">Loading activity data...</p>
            </div>
          </div>
        ) : !hasData ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-muted/30 rounded-full flex items-center justify-center mx-auto">
                <Icon name={activeOption?.icon} size={20} className="text-muted-foreground" />
              </div>
              <p className="wellness-text-secondary text-sm">No {activeOption?.label?.toLowerCase()} data this week</p>
              <p className="text-xs text-muted-foreground">Log activities to see your trends</p>
            </div>
          </div>
        ) : (
          <div className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              {activeChart === 'steps' || activeChart === 'calories' ? (
                <BarChart 
                  data={weeklyData} 
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="hsl(var(--border))" 
                    opacity={0.3} 
                  />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey={activeChart} 
                    fill={activeOption?.color}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              ) : (
                <LineChart 
                  data={weeklyData} 
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="hsl(var(--border))" 
                    opacity={0.3} 
                  />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey={activeChart} 
                    stroke={activeOption?.color}
                    strokeWidth={3}
                    dot={{ 
                      fill: activeOption?.color, 
                      strokeWidth: 2, 
                      r: 4,
                      stroke: '#fff'
                    }}
                    activeDot={{ 
                      r: 6, 
                      stroke: activeOption?.color, 
                      strokeWidth: 2,
                      fill: '#fff'
                    }}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {/* Weekly Summary Footer */}
      <div className="flex-shrink-0 pt-4 border-t border-wellness-light min-h-[80px]">
        <div className="grid grid-cols-3 wellness-gap-sm text-center">
          <div className="space-y-1">
            <p className="wellness-text-secondary">This Week</p>
            <p className="wellness-text-accent text-constrained">
              {weeklyData
                ?.reduce((sum, day) => sum + (day?.[activeChart] || 0), 0)
                ?.toLocaleString()}
            </p>
          </div>
          <div className="space-y-1">
            <p className="wellness-text-secondary">Daily Avg</p>
            <p className="wellness-text-accent text-constrained">
              {Math.round(
                weeklyData?.reduce((sum, day) => sum + (day?.[activeChart] || 0), 0) / 7
              )?.toLocaleString()}
            </p>
          </div>
          <div className="space-y-1">
            <p className="wellness-text-secondary">Best Day</p>
            <p className="font-medium text-constrained" style={{ color: activeOption?.color }}>
              {hasData
                ? weeklyData?.reduce((best, day) =>
                    (day?.[activeChart] || 0) > (best?.[activeChart] || 0) ? day : best
                  )?.day
                : '—'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyChart;